<template>
  <vue-dropzone
    id="dropzone"
    ref="myVueDropzone"
    :options="dropzoneOptions"
    :class="customClass"
  />
</template>
<script>
import vue2Dropzone from 'vue2-dropzone'
import vuetifyToast from 'vuetify-toast'
import { apiAxios } from '@/helpers'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'

let status = ''

export default {
  name: 'VideoUploader',
  components: {
    vueDropzone: vue2Dropzone,
  },
  props: {
    objectId: {
      type: String,
      required: true,
      default: '',
    },
    typeVideo: {
      type: String,
      required: false,
      default: 'match',
    },
  },
  data: function () {
    return {
      dropzoneOptions: {
        url: '/placeholder', // We'll handle uploads manually
        thumbnailWidth: 150,
        maxFilesize: 10 * 1024, // 10GB in MB
        acceptedFiles: 'video/*',
        maxFiles: 1,
        autoProcessQueue: false, // Important: we handle this manually
        addRemoveLinks: true,
        dictCancelUpload: this.$t('titles.dropzone.dictCancelUpload'),
        dictCancelUploadConfirmation: this.$t(
          'titles.dropzone.dictCancelUploadConfirmation'
        ),
        //dictRemoveFile: this.$t('titles.dropzone.dictRemoveFile'),
        dictMaxFilesExceeded: this.$t('titles.dropzone.dictMaxFilesExceeded'),
        dictResponseError: this.$t('titles.dropzone.dictResponseError'),
        dictInvalidFileType: this.$t('titles.dropzone.dictInvalidFileType'),
        dictFileTooBig: this.$t('titles.dropzone.dictFileTooBig'),
        dictDefaultMessage: this.$t('titles.dropzone.dictDefaultMessage'),
        init: function () {
          const dz = this
          dz.vueComponent = dz.element.__vue__

          this.on('error', function (file, error) {
            status = 'error'
            vuetifyToast.show({
              text: error,
              color: 'error',
              timeout: 5000,
              icon: 'warning',
            })
          })

          this.on('addedfile', function (file) {
            const vm = dz.vueComponent
            file.manuallyRemoved = false

            // add custom flag for user delete
            const removeButton = file.previewElement.querySelector('.dz-remove')
            if (removeButton) {
              removeButton.addEventListener('click', function (e) {
                e.preventDefault() // Stop dropzone's default removal
                e.stopPropagation()

                file.manuallyRemoved = true

                // Now manually remove the file (this will trigger removedfile event)
                dz.removeFile(file)
              })
            }

            // Check if file is large enough for multipart upload
            const useMultipart = true //file.size > 100 * 1024 * 1024 // 100MB threshold

            if (useMultipart) {
              vm.handleMultipartUpload(file, dz)
            } else {
              vm.handleSimpleUpload(file, dz)
            }
          })

          this.on('uploadprogress', function (/* file, progress */) {
            // This will be triggered by our manual progress updates
          })

          this.on('success', function (/* file */) {
            status = 'success'
            vuetifyToast.show({
              text: 'Upload completed successfully',
              color: 'success',
              timeout: 5000,
              icon: 'check',
            })
          })

          this.on('complete', function (file) {
            const vm = dz.vueComponent
            status = ''
            vm.activeUploads.delete(file)
          })

          dz.on('removedfile', async function (file) {
            const vm = dz.vueComponent

            if (vm.activeUploads.has(file)) {
              console.log('Aborting active upload.')
              vm.abortMultipartUpload(file, dz, 'User removed the file')
            } else if (
              file.status === 'success' &&
              file.location &&
              file.manuallyRemoved == true
            ) {
              console.log('Removing file from storage.')
              await vm.removeS3File(file, dz)
              vm.$emit('reload') // reload page
            }
          })
        },
      },
      status: '',
      activeUploads: new Map(), // Track active multipart uploads
    }
  },
  computed: {
    customClass() {
      if (status == 'success') return 'successUpload'
      else if (status == 'error') return 'errorUpload'
      return ''
    },
  },
  methods: {
    renameFile(file) {
      let ext = file.name.split('.')
      ext = ext.length ? ext[ext.length - 1] : ''

      let filename = this.objectId || 'file_' + Date.now()

      if (ext) filename += '.' + ext
      return filename
    },

    // Handle simple uploads for smaller files (existing logic)
    async handleSimpleUpload(file, dropzoneInstance) {
      const renamedFile = this.renameFile(file)

      try {
        const { data } = await apiAxios.get('/upload/presigned-url', {
          params: {
            fileName: renamedFile,
            fileType: file.type,
          },
          timeout: 30000, // 30 second timeout
        })

        // Update the dropzone URL and process
        dropzoneInstance.options.url = data.data.url
        dropzoneInstance.options.method = 'put'
        dropzoneInstance.options.timeout = 300000 // 5 minute timeout
        dropzoneInstance.processQueue()
      } catch (err) {
        console.error('Error getting simple upload URL:', err)
        this.showError('Error getting upload URL')
        dropzoneInstance.emit('error', file, 'Failed to get upload URL')
      }
    },

    // Handle multipart uploads for large files
    async handleMultipartUpload(file, dropzoneInstance) {
      const renamedFile = this.renameFile(file)

      try {
        // Step 1: Initiate multipart upload
        const initResponse = await apiAxios.post(
          '/upload/file/s3/multipart/init',
          {
            fileName: renamedFile,
            fileType: file.type,
          }
        )

        const { uploadId, key } = initResponse.data.data

        // Store upload info
        this.activeUploads.set(file, {
          uploadId,
          key,
          parts: [],
          totalSize: file.size,
        })

        // Step 2: Upload file in chunks
        await this.uploadFileInParts(file, dropzoneInstance)
      } catch (err) {
        console.error('Error in multipart upload:', err)
        this.showError('Error starting multipart upload')
        dropzoneInstance.emit('error', file, 'Multipart upload failed')
      }
    },

    async uploadFileInParts(file, dropzoneInstance) {
      const uploadInfo = this.activeUploads.get(file)
      const partSize = 20 * 1024 * 1024 // 20MB parts
      const totalParts = Math.ceil(file.size / partSize)
      const concurrency = 3 // how many parts to upload at the same time

      try {
        let currentPart = 0

        const uploadPart = async (partNumber) => {
          const start = (partNumber - 1) * partSize
          const end = Math.min(start + partSize, file.size)
          const chunk = file.slice(start, end)

          // Get presigned URL
          const urlResponse = await apiAxios.post(
            '/upload/file/s3/multipart/part',
            {
              uploadId: uploadInfo.uploadId,
              key: uploadInfo.key,
              partNumber,
            }
          )

          const { presignedUrl } = urlResponse.data.data

          // Upload to S3
          const uploadResponse = await fetch(presignedUrl, {
            method: 'PUT',
            body: chunk,
            headers: {
              'Content-Type': file.type,
            },
          })

          if (!uploadResponse.ok) {
            throw new Error(`Failed to upload part ${partNumber}`)
          }

          const etag = uploadResponse.headers.get('ETag')
          uploadInfo.parts.push({ PartNumber: partNumber, ETag: etag })

          // Emit progress (based on finished parts)
          const finishedParts = uploadInfo.parts.length
          const progress = (finishedParts / totalParts) * 100
          dropzoneInstance.emit('uploadprogress', file, progress)
        }

        // Worker function to keep launching uploads until all parts are done
        const worker = async () => {
          while (currentPart < totalParts) {
            currentPart++
            await uploadPart(currentPart)
          }
        }

        // Launch `concurrency` workers in parallel
        await Promise.all(new Array(concurrency).fill(0).map(worker))

        // Step 3: Complete multipart upload
        await this.completeMultipartUpload(file, dropzoneInstance)
      } catch (error) {
        console.error('Error uploading parts:', error)
        await this.abortMultipartUpload(file, dropzoneInstance, error.message)
      }
    },

    async completeMultipartUpload(file, dropzoneInstance) {
      const uploadInfo = this.activeUploads.get(file)

      try {
        const completeResponse = await apiAxios.post(
          '/upload/file/s3/multipart/complete',
          {
            uploadId: uploadInfo.uploadId,
            key: uploadInfo.key,
            parts: uploadInfo.parts.sort((a, b) => a.PartNumber - b.PartNumber),
          }
        )

        if (completeResponse.data.data.success) {
          // Notify about successful upload completion
          await this.notifyUploadComplete(
            uploadInfo.key,
            completeResponse.data.data.location,
            file,
            dropzoneInstance
          )
        } else {
          throw new Error('Failed to complete multipart upload')
        }
      } catch (error) {
        console.error('Error completing multipart upload:', error)
        await this.abortMultipartUpload(file, dropzoneInstance, error.message)
      }
    },

    async abortMultipartUpload(file, dropzoneInstance, message) {
      const uploadInfo = this.activeUploads.get(file)
      if (!uploadInfo) return

      try {
        await apiAxios.post('/upload/file/s3/multipart/abort', {
          uploadId: uploadInfo.uploadId,
          key: uploadInfo.key,
        })
      } catch (error) {
        console.error('Error aborting multipart upload:', error)
      } finally {
        if (dropzoneInstance && message)
          dropzoneInstance.emit('error', file, message)
        dropzoneInstance.emit('complete', file)
      }
    },

    async notifyUploadComplete(key, location, file, dropzoneInstance) {
      try {
        await apiAxios
          .post(`/upload/file/s3/multipart/done/${this.objectId}`, {
            type: this.typeVideo,
            location,
          })
          .then((/* { data } */) => {
            /* vuetifyToast.show({
              text: data.message,
              color: 'info',
              timeout: 3000,
              icon: 'check',
            }) */
            dropzoneInstance.emit('success', file, 'success', null)
            file.status = 'success' // ensure Dropzone knows this is not canceled, prevent automatic error message if status is not 'success'
            file.location = decodeURIComponent(location)
            dropzoneInstance.emit('complete', file)
            // this.$emit('upload') // autoclose and refresh
            this.$emit('reload') // reload page
          })
        // .catch((error) => {
        // TODO
        // delete file on s3
        // emit error
        // emit completed
        // })
      } catch (error) {
        // do not set an dropzoneInstance.emit('error') state here. its a server error at this point
        console.error('Error notifying upload completion:', error)
        dropzoneInstance.emit('complete', file)
      }
    },

    async removeS3File(file, dropzoneInstance) {
      //broken in the backend, need new api
      try {
        await apiAxios.delete('/file', {
          data: {
            cdn: `${process.env.VUE_APP_ASSETS_URL}${
              process.env.VUE_APP_ASSETS_VIDEO_PATH
            }${file.location.split(process.env.VUE_APP_ASSETS_VIDEO_PATH)[1]}`,
          },
        })
      } catch (error) {
        dropzoneInstance.emit('error', file, error)
      }
    },

    showError(message) {
      vuetifyToast.show({
        text: message,
        color: 'error',
        timeout: 5000,
        icon: 'warning',
      })
    },
  },

  mounted() {
    status = ''
  },
  beforeDestroy() {
    // Cleanup any active uploads
    for (const [file] of this.activeUploads) {
      this.abortMultipartUpload(file)
    }
  },
}
</script>
<style lang="scss">
.vue-dropzone > .dz-preview .dz-details {
  background-color: var(--v-primary-base);
}
.successUpload > .dz-preview .dz-details {
  background-color: var(--v-primary-base) !important;
}
.errorUpload > .dz-preview .dz-details {
  background-color: var(--v-error-base) !important;
}
</style>
